import { addDays, zonedDay } from './format.js';

/**
 * A DUE date inside the lead time cannot be met by anyone. The prefill
 * already avoids it; this is the same arithmetic applied to whatever is
 * typed afterwards.
 *
 * Deliberately not applied to the requested date. That one records the day
 * the request was RAISED — today, or a past day for an order being written up
 * late. It is never a target, so "too soon to be achievable" is meaningless
 * there, and applying the check made every freshly-raised request warn about
 * itself.
 *
 * `todayYmd` comes from the SITE's calendar, not the viewer's. `.toISOString()`
 * here meant UTC: between 15:00 and midnight UTC a Tokyo site was already on
 * tomorrow, so `earliest` came out a day early and a genuinely unmeetable due
 * date passed the check in silence. `orderPrefill` seeds this same field
 * through `zonedDay(..., tz)` — the check has to agree with the seed.
 *
 * Both are bare calendar days, so the comparison is a plain string compare.
 */
export function computeEarliest(tz: string, lead: number | null): string | null {
  if (lead == null) return null;
  const todayYmd = zonedDay(new Date().toISOString(), tz);
  return addDays(todayYmd, lead);
}

export function isTooSoon(value: string, earliest: string | null): boolean {
  return earliest != null && value !== '' && value < earliest;
}

/** Why a date breaks the order's date sequence — the per-field codes the form
 *  shows, mirroring the server's rule:
 *
 *    needs-requested   a reply or an ETA with no requested date to follow
 *    before-requested  earlier than the requested date
 *    before-replied    the ETA is earlier than the replied-by date */
export type DateOrderError = 'needs-requested' | 'before-requested' | 'before-replied';

export interface IDateOrderErrors {
  answeredDate?: DateOrderError;
  dueDate?: DateOrderError;
}

/**
 * The order's three dates against requested ≤ replied-by ≤ ETA (and
 * requested ≤ ETA when there is no reply). Blank is '' and never wrong on its
 * own. All three are bare `YYYY-MM-DD` days, so a string compare is exact. An
 * empty object means the dates are in order.
 */
export function dateOrderErrors(dates: {
  requestedDate: string;
  answeredDate: string;
  dueDate: string;
}): IDateOrderErrors {
  const { requestedDate, answeredDate, dueDate } = dates;
  const out: IDateOrderErrors = {};
  if (answeredDate !== '') {
    if (requestedDate === '') out.answeredDate = 'needs-requested';
    else if (answeredDate < requestedDate) out.answeredDate = 'before-requested';
  }
  if (dueDate !== '') {
    if (requestedDate === '') out.dueDate = 'needs-requested';
    else if (dueDate < requestedDate) out.dueDate = 'before-requested';
    else if (answeredDate !== '' && dueDate < answeredDate) out.dueDate = 'before-replied';
  }
  return out;
}
